import { PerformanceLogItem } from "./performance-logs.types";

/**
 * CSV export helpers for the Performance Logs tool.
 *
 * Implements the flattening / encoding algorithm specified in
 * docs/performance-logs/06-export-format.md:
 *  - depth-first leaf extraction with `parent.child` / `arr[i]` path notation;
 *  - column set = first-seen union of all leaf paths across records;
 *  - RFC 4180 quoting, CRLF row terminator, UTF-8 BOM.
 *
 * The functions are pure so they can be unit-tested independently of React.
 */

export interface FlattenResult {
  /** Column paths in first-seen order (the CSV header row). */
  header: string[];
  /** One serialized string row per input record, aligned to `header`. */
  rows: string[][];
}

interface LeafPair {
  path: string;
  value: string;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Serialize a leaf value to its CSV cell text (before quoting).
 *  - string  -> as-is
 *  - number  -> decimal text (no thousands separator)
 *  - boolean -> "true" / "false"
 *  - null / undefined -> empty cell
 */
function serializeLeaf(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  // Objects/arrays are never leaves; defensive fallback only.
  return String(value);
}

/**
 * Traverse a node depth-first, appending one (path -> value) pair per leaf.
 * Empty objects and empty arrays contribute nothing.
 */
function collectLeaves(node: unknown, path: string, out: LeafPair[]): void {
  if (Array.isArray(node)) {
    node.forEach((element, index) => collectLeaves(element, `${path}[${index}]`, out));
    return;
  }
  if (isPlainObject(node)) {
    Object.keys(node).forEach((key) => {
      const childPath = path ? `${path}.${key}` : key;
      collectLeaves(node[key], childPath, out);
    });
    return;
  }
  // Primitive or null => leaf at the current path.
  out.push({ path, value: serializeLeaf(node) });
}

/**
 * Flatten records into a deterministic header + aligned rows.
 *
 * Column ordering: iterate records in order; for each, traverse depth-first in
 * property/index order; append each leaf path the first time it is seen.
 */
export function flattenRecords(records: PerformanceLogItem[]): FlattenResult {
  const header: string[] = [];
  const seen = new Set<string>();
  const perRecord: Map<string, string>[] = [];

  records.forEach((record) => {
    const leaves: LeafPair[] = [];
    collectLeaves(record, "", leaves);

    const values = new Map<string, string>();
    leaves.forEach(({ path, value }) => {
      if (!seen.has(path)) {
        seen.add(path);
        header.push(path);
      }
      if (!values.has(path)) {
        values.set(path, value);
      }
    });

    perRecord.push(values);
  });

  const rows = perRecord.map((values) => header.map((column) => values.get(column) ?? ""));
  return { header, rows };
}

/**
 * Wrap a field in double quotes iff it contains a comma, double quote, CR or LF,
 * doubling any embedded double quote (RFC 4180).
 */
function escapeField(field: string): string {
  if (/[",\r\n]/.test(field)) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

/**
 * Build the full CSV text (UTF-8 BOM prefix, CRLF terminators) for the given
 * records. An empty dataset produces just the BOM and a single CRLF.
 */
export function buildCsv(records: PerformanceLogItem[]): string {
  const { header, rows } = flattenRecords(records);
  const crlf = "\r\n";

  const lines: string[] = [header.map(escapeField).join(",")];
  rows.forEach((row) => lines.push(row.map(escapeField).join(",")));

  return `\uFEFF${lines.join(crlf)}${crlf}`;
}
